import React, {useCallback, useEffect, useRef} from 'react';

import equal from 'fast-deep-equal/es6/react';
import {i18n} from '@netx/core/i18n';
import {useRefState, useComponentIsMounted} from '@netx/core-components/hooks';
import {isEmptyValue, valuesMatch} from '../utils';

export type FormAction = string;
export type FormActions<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> = {
	[Property in keyof TValues]: TAction | undefined;
};

export type FormChanges<TValues extends FormValues = FormValues> = Partial<
	Record<keyof TValues, boolean>
>;

export type FormFieldRef<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> = Record<
	keyof TValues,
	{
		name: keyof TValues;
		options: FormFieldRefOptions<TValues, TAction>;
	}
>;

export interface FormFieldRefOptions<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
> {
	validator?: (
		value: any,
		values?: TValues,
	) => true | Error | string | Promise<true | Error | string>;
	hasChanged?: (
		value: any,
		originalValue: any,
		multipleValues: boolean,
		action?: TAction,
	) => boolean;
	required?: boolean;
	value?: any;
	action?: TAction;
}

export type FormMultiFields<TValues extends FormValues = FormValues> = Record<
	keyof TValues,
	any
>;
export type FormMultipleValues<TValues extends FormValues = FormValues> =
	Record<keyof TValues, boolean>;

export interface FormOptions<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
	TReturn = any,
> {
	/** Validate regardless if field has changed */
	alwaysValidate?: boolean;
	multipleValues?: FormMultipleValues<TValues>;
	formatSubmissionError?: (ex: any) => any;
	onChange?: (hasChanged: boolean, isValid: boolean) => void;
	onReset?: () => void;
	onSubmit?: (
		values: TValues,
		changes?: FormChanges<TValues>,
		actions?: FormActions<TValues, TAction>,
	) => Promise<TReturn> | void;
	resetOnSubmit?: boolean;
	resetWithCurrentValues?: boolean;
	/** wins over `resetWithCurrentValues` */
	resetWithOriginalValues?: boolean;
	submitLabel?: string;
	removeValueOnUnregister?: boolean;
}

export type FormValidationError = {
	message: string;
	type: string;
};

export type FormValidationErrors<TValues = FormValues> = Record<
	keyof TValues,
	FormValidationError
>;

export type FormValues = Record<string | number | symbol, any>;

const DEFAULT_OPTIONS: FormOptions = {
	alwaysValidate: false,
	removeValueOnUnregister: true,
	resetOnSubmit: false,
	resetWithOriginalValues: false,
	submitLabel: i18n.t('i18n.submit'),
};

/**
 * @deprecated use react-hook-form instead
 */
export const useForm = <
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
	TReturn = any,
>(
	values?: TValues,
	formOptions?: FormOptions<TValues, TAction, TReturn>,
) => {
	const {act, shouldAct} = useComponentIsMounted();

	const [options, optionsRef, setOptions] = useRefState({
		...DEFAULT_OPTIONS,
		...formOptions,
	} as FormOptions<TValues>);

	const lastFormOptionsRef = useRef(formOptions);
	useEffect(() => {
		if (equal(lastFormOptionsRef.current, formOptions)) return;
		lastFormOptionsRef.current = formOptions || {};
		act(() =>
			setOptions({
				...DEFAULT_OPTIONS,
				...formOptions,
			} as FormOptions<TValues>),
		);
	}, [formOptions]);

	const multiFieldsRef = useRef({} as FormMultiFields<TValues>);
	const fieldRef = useRef({} as FormFieldRef<TValues, TAction>);

	const [isSubmitting, isSubmittingRef, setIsSubmitting] = useRefState(false);
	const [submissionError, submissionErrorRef, setSubmissionError] = useRefState<
		string | null
	>(null);

	const [formMultipleValues, formMultipleValuesRef, setFormMultipleValues] =
		useRefState(options.multipleValues || ({} as FormMultipleValues<TValues>));
	const [formValues, formValuesRef, setFormValues] = useRefState<TValues>(
		values || ({} as TValues),
	);
	const [originalValues, originalValuesRef, setOriginalValues] = useRefState(
		formValues as TValues,
	);
	const [formActions, formActionsRef, setFormActions] = useRefState(
		{} as FormActions<TValues, TAction>,
	);
	const [formChanges, formChangesRef, setFormChanges] = useRefState(
		{} as FormChanges<TValues>,
	);
	const [formErrors, formErrorsRef, setFormErrors] = useRefState(
		{} as FormValidationErrors<TValues>,
	);
	const [hasChanged, hasChangedRef, setHasChanged] = useRefState(false);
	const [isValid, isValidRef, setIsValid] = useRefState(true);

	// Setup/teardown
	useEffect(() => {
		return () => {
			multiFieldsRef.current = {} as FormMultiFields<TValues>;
			fieldRef.current = {} as FormFieldRef<TValues, TAction>;
		};
	}, []);

	// When values passed in change, merge with current
	const lastValuesRef = useRef(values);
	useEffect(() => {
		if (equal(lastValuesRef.current, values)) return;
		lastValuesRef.current = values;
		setOriginalValues(values || ({} as TValues));
		setFormValues(values || ({} as TValues));
	}, [values]);

	// When multiple values passed in change
	useEffect(() => {
		if (equal(formMultipleValuesRef.current, options.multipleValues)) return;
		setFormMultipleValues(
			options.multipleValues || ({} as FormMultipleValues<TValues>),
		);
	}, [options.multipleValues]);

	// When our data/validation changes
	useEffect(() => {
		const {onChange} = optionsRef.current;
		onChange && onChange(hasChanged, isValid);
	}, [hasChanged, isValid]);

	// When changes occur
	useEffect(() => {
		setHasChanged(!!Object.keys(formChanges).length);
	}, [formChanges]);

	// When errors change
	useEffect(() => {
		setIsValid(!Object.keys(formErrors).length);
	}, [formErrors]);

	/** Get action for field */
	const getAction = useCallback(
		<T extends keyof TValues>(fieldName: T): TAction | undefined => {
			return formActionsRef.current[fieldName];
		},
		[],
	);

	/** Field has error */
	const hasError = useCallback(
		<T extends keyof TValues>(fieldName: T, type: string, message: string) => {
			const existing = formErrorsRef.current[fieldName];
			return existing && existing.type === type && existing.message === message;
		},
		[],
	);

	/** Field(s) have error */
	const inErrors = useCallback(
		<T extends keyof TValues>(fieldNames: T | T[]) => {
			if (!Array.isArray(fieldNames)) fieldNames = [fieldNames];
			return Object.keys(formErrorsRef.current).some((key) =>
				(fieldNames as Array<keyof TValues>).includes(key),
			);
		},
		[],
	);

	/** Check if field has changed */
	const checkFieldChanged = useCallback(
		<T extends keyof TValues, A extends TAction>(
			fieldName: T,
			value: any,
			originalValue: any,
			multipleValues: boolean,
			action?: A,
		): boolean => {
			const field = fieldRef.current[fieldName];
			if (!field) return false;

			const {hasChanged} = field.options;

			// Field did not provide its own method
			if (!hasChanged) {
				return !valuesMatch(value, originalValue, multipleValues);
			}

			return hasChanged(value, originalValue, multipleValues, action);
		},
		[],
	);

	/** Clear action for field */
	const clearAction = useCallback(
		<T extends keyof TValues>(fieldName: T): void => {
			if (!fieldRef.current[fieldName]) return;

			setFormActions((actions) => {
				const _actions = {...actions};
				delete _actions[fieldName];
				return _actions;
			});
		},
		[],
	);

	/** Clear error for field(s) */
	const clearError = useCallback(
		<T extends keyof TValues>(fieldNames?: T | T[]) => {
			if (fieldNames && !inErrors(fieldNames)) return;

			setFormErrors((errors) => {
				// Clear all
				if (fieldNames == null) return {} as FormValidationErrors<TValues>;

				if (!Array.isArray(fieldNames)) fieldNames = [fieldNames];

				const newErrors = {...errors};
				fieldNames.forEach((fieldName) => {
					delete newErrors[fieldName];
				});
				return Object.keys(newErrors).length > 0
					? newErrors
					: ({} as FormValidationErrors<TValues>);
			});
		},
		[inErrors],
	);

	/** Reset actions */
	const resetActions = useCallback(<A extends TAction>(withAction?: A) => {
		const actions = {} as FormActions<TValues, TAction>;
		Object.keys(fieldRef.current).forEach((fieldName: keyof TValues) => {
			actions[fieldName] = withAction;
		});
		setFormActions(actions);
	}, []);

	/** Reset all changes */
	const resetChanges = useCallback(() => {
		setFormChanges({} as FormChanges<TValues>);
	}, []);

	/** Set/unset change for field */
	const setChange = useCallback(
		<T extends keyof TValues>(fieldName: T, changed: boolean) => {
			if (!fieldRef.current[fieldName]) return;

			setFormChanges((changes) => {
				const _changes = {...changes};
				changed ? (_changes[fieldName] = true) : delete _changes[fieldName];
				return _changes;
			});
		},
		[],
	);

	/** Set error for field */
	const setError = useCallback(
		<T extends keyof TValues>(fieldName: T, type: string, message: string) => {
			// Already have an error set
			if (hasError(fieldName, type, message)) return;

			setFormErrors((errors) => {
				return {
					...errors,
					[fieldName]: {
						message,
						type,
					},
				};
			});
		},
		[hasError],
	);

	/** Validate single field */
	const validateField = useCallback(
		async <T extends keyof TValues>(fieldName: T, value?: any) => {
			const input = fieldRef.current[fieldName];
			if (!input) return new Error(`'${fieldName as string}' not registered`);

			// No value was passed in, use the value we already have stored
			if (value === void 0) {
				value = formValuesRef.current[fieldName];
			}

			const {required, validator} = input.options;
			let result: true | Error | string = true;

			if (validator) {
				result = await validator(value, formValuesRef.current);
			}

			if (result === true && required && isEmptyValue(value)) {
				result = new Error(i18n.t('i18n.required'));
			}

			result === true
				? clearError(fieldName)
				: setError(
						fieldName,
						'validation',
						typeof result === 'string' ? result : result.message,
				  );

			return result;
		},
		[clearError, setError],
	);

	/** Validate all fields in form */
	const validate = useCallback(async () => {
		const fieldNames = Object.keys(fieldRef.current);
		const currentValues = formValuesRef.current;

		let validationFailed = false;

		for (let i = 0, c = fieldNames.length; i < c; i++) {
			const fieldName = fieldNames[i];
			const valid = await validateField(fieldName, currentValues[fieldName]);
			if (valid !== true) validationFailed = true;
		}

		return !validationFailed;
	}, [validateField]);

	useEffect(() => {
		if (isValidRef.current || !shouldAct()) return;
		Object.keys(formErrorsRef.current).forEach((fieldName) =>
			validateField(fieldName),
		);
	}, [formValues]);

	/** Register field */
	const register = useCallback(
		<T extends keyof TValues, O extends FormFieldRefOptions<TValues, TAction>>(
			fieldName: T,
			fieldOptions: O = {} as O,
		) => {
			if (!fieldName) return () => {};

			const {current} = multiFieldsRef;
			const {alwaysValidate} = optionsRef.current;

			if (current[fieldName] != null) {
				const input = fieldRef.current[fieldName];
				// Always update with most recent options
				input.options = {
					...input.options,
					...options,
				};
				current[fieldName]++;
				alwaysValidate && validateField(fieldName);
				return () => unregister(fieldName);
			}

			current[fieldName] = 1;
			fieldRef.current[fieldName] = {
				name: fieldName,
				options: fieldOptions,
			};
			fieldOptions.value !== void 0 && setValue(fieldName, fieldOptions.value);
			fieldOptions.action !== void 0 &&
				setAction(fieldName, fieldOptions.action);
			alwaysValidate && validateField(fieldName);

			return () => unregister(fieldName);
		},
		[validateField],
	);

	const removeAction = useCallback(<T extends keyof TValues>(fieldName: T) => {
		if (!fieldRef.current[fieldName]) return;
		setFormActions((actions) => {
			const _actions = {...actions};
			delete _actions[fieldName];
			return _actions;
		});
	}, []);

	/** Set action for field */
	const setAction = useCallback(
		<T extends keyof TValues, A extends TAction>(
			fieldName: T,
			action: A,
		): void => {
			if (!fieldRef.current[fieldName]) return;
			setFormActions((actions) => ({
				...actions,
				[fieldName]: action,
			}));
		},
		[],
	);

	/** Set field(s) value */
	const setValue = useCallback(
		<T extends keyof TValues | Partial<TValues>>(
			fieldName: T,
			value?: [T] extends [keyof TValues] ? TValues[T] : undefined,
		) => {
			// An object of values
			if (typeof fieldName !== 'string') {
				(Object.keys(fieldName) as unknown as Array<keyof TValues>).forEach(
					(name) => {
						setValue(name, (fieldName as Partial<TValues>)[name]);
					},
				);
				return;
			}

			// TODO: maybe we do not require registering of fields if we want to allow this
			// to be used as a headless form like solution
			// But I set it up this way with the expectation that you would send information
			// about the fields this will track (validation etc) - so it really is not that
			// outrageous to expect someone to take the time to simply call
			// `form.register('foo', {required: true})`
			// This form does allow for passing in values at init that will be passed back out
			// on submit - just will not allow the manipulation of the values if they are not registered
			// IDK - something to think about (something i was thinking about while writing tests)
			if (!fieldRef.current[fieldName]) return;

			const {alwaysValidate} = optionsRef.current;

			const changed = checkFieldChanged(
				fieldName,
				value,
				originalValuesRef.current[fieldName as string],
				formMultipleValuesRef.current[fieldName],
				formActionsRef.current[fieldName],
			);
			setChange(fieldName, changed);

			// No need to go further if the value is the same as the currently set one
			if (!changed && valuesMatch(formValuesRef.current[fieldName], value))
				return;

			// Let's assume that if the field has not changed that there are no errors
			// The component using this should opt to validate the form before submitting
			// at which point "original values" that fail validation will be caught
			// If using the submit handler we offer, validation will be called before calling submit
			// method in component
			changed || alwaysValidate
				? validateField(fieldName, value)
				: clearError(fieldName);

			setFormValues((values) => ({
				...values,
				[fieldName]: value,
			}));
		},
		[checkFieldChanged, clearError, setChange, validateField],
	);

	/** Un-register field */
	const unregister = useCallback(
		<T extends keyof TValues>(fieldName: T) => {
			// Unregister all fields
			if (!fieldName) {
				clearError();
				resetChanges();
				setFormValues({} as TValues);
				fieldRef.current = {} as FormFieldRef<TValues, TAction>;
				return;
			}

			// We have no records of ever registering this field even once
			const {current} = multiFieldsRef;
			if (!current[fieldName] == null) return;

			const {removeValueOnUnregister} = optionsRef.current;

			// If it is the only record, delete
			if (current[fieldName] === 1) {
				delete current[fieldName];
				clearError(fieldName);
				setChange(fieldName, false);
				removeAction(fieldName);
				// Remove from values
				removeValueOnUnregister &&
					setFormValues((formValues) => {
						const values = {...formValues};
						delete values[fieldName];
						return values;
					});
				delete fieldRef.current[fieldName];
				return;
			}
			// Otherwise just reduce the count
			current[fieldName]--;
		},
		[clearError, resetChanges, setChange, validate],
	);

	const updateOptions = useCallback(
		<T extends keyof TValues, O extends FormFieldRefOptions<TValues, TAction>>(
			fieldName: keyof T,
			options: O,
		) => {
			if (!fieldRef.current[fieldName]) return;
			fieldRef.current[fieldName].options = {
				...fieldRef.current[fieldName].options,
				...options,
			};
		},
		[],
	);

	/** Reset form */
	const reset = useCallback(
		<
			T extends Partial<TValues>,
			A extends TAction,
			M extends FormMultipleValues<TValues>,
		>(
			withValues?: Partial<T>,
			withAction?: A,
			withMultipleValues?: M,
		) => {
			const values: FormValues = {};
			// Reset all to null
			Object.keys(fieldRef.current).forEach((name) => {
				values[name] = null;
			});
			// Combine with any passed in values
			setFormValues({...values, ...withValues} as TValues);
			setSubmissionError(null);
			setFormErrors({} as FormValidationErrors<TValues>);
			setOriginalValues({...values, ...withValues} as TValues);
			setFormMultipleValues((withMultipleValues || {}) as TValues);
			withAction ? resetActions(withAction) : resetActions();
			clearError();
			resetChanges();

			const {onReset} = optionsRef.current;
			onReset && onReset();
		},
		[clearError, resetActions, resetChanges],
	);

	const handleSubmit = useCallback(
		<
				CB extends (
					values: TValues,
					changes?: FormChanges<TValues>,
					actions?: FormActions<TValues, TAction>,
				) => Promise<TReturn> | void,
			>(
				callback: CB = options.onSubmit as CB,
			) =>
			async (event?: React.FormEvent<HTMLFormElement>) => {
				if (event) {
					event.preventDefault();
					event.stopPropagation();
				}

				(document.activeElement as HTMLInputElement)?.blur?.();

				if (isSubmittingRef.current) return;
				setSubmissionError(null);
				setIsSubmitting(true);

				try {
					const valid = await validate();

					// Check that we are still mounted
					if (!shouldAct()) return;

					if (!valid) return false;

					const {resetOnSubmit} = optionsRef.current;

					const result = callback
						? await callback(
								formValuesRef.current,
								formChangesRef.current,
								formActionsRef.current,
						  )
						: undefined;

					act(() => {
						if (resetOnSubmit) {
							const {resetWithCurrentValues, resetWithOriginalValues} =
								optionsRef.current;
							reset(
								resetWithOriginalValues
									? originalValuesRef.current
									: resetWithCurrentValues
									? formValuesRef.current
									: undefined,
							);
						} else {
							setOriginalValues(formValuesRef.current);
							const multipleValues = {...formMultipleValuesRef.current};
							for (const key in formChangesRef.current) {
								delete multipleValues[key];
							}
							setFormMultipleValues(multipleValues);
							resetChanges();
						}
					});

					return result as TReturn | undefined;
				} catch (ex) {
					setSubmissionError(
						options.formatSubmissionError
							? options.formatSubmissionError(ex)
							: (ex as Error).message,
					);
					// Do nothing (for now) - the submission was just not successful
				} finally {
					act(() => setIsSubmitting(false));
				}
			},
		[
			reset,
			resetChanges,
			validate,
			options.onSubmit,
			options.formatSubmissionError,
		],
	);

	return {
		fieldRef,
		options,

		actions: formActions,
		actionsRef: formActionsRef,
		clearAction,
		getAction,
		setAction,
		resetActions,
		setSubmissionError,
		submissionError,

		changes: formChanges,
		changesRef: formChangesRef,
		hasChanged,
		hasChangedRef,
		setChange,
		resetChanges,
		checkFieldChanged,

		errors: formErrors,
		errorsRef: formErrorsRef,
		setError,
		clearError,
		isValid,
		isValidRef,
		validate,
		validateField,

		register,
		unregister,
		handleSubmit,
		reset,
		isSubmitting,

		multipleValues: formMultipleValues,
		multipleValuesRef: formMultipleValuesRef,
		setMultipleValues: setFormMultipleValues,

		originalValues,
		originalValuesRef,
		setValue,
		setValues: setFormValues,
		updateOptions,
		values: formValues,
		valuesRef: formValuesRef,
	};
};

// Typescript cannot infer `ReturnType` of a generic, so you must roll your own
class InferWrapper<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
	TReturn = any,
> {
	mediate = (...args: any[]) => useForm<TValues, TAction, TReturn>(...args);
}

export type FormType<
	TValues extends FormValues = FormValues,
	TAction extends FormAction = FormAction,
	TReturn = any,
> = ReturnType<InferWrapper<TValues, TAction, TReturn>['mediate']>;

export type FormParameters<TValues extends FormValues = FormValues> = [
	(TValues | undefined)?,
	(FormOptions<TValues> | undefined)?,
];

export default useForm;
